/* eslint-disable react/jsx-no-useless-fragment */
import React, { Fragment, useRef } from 'react';
import {
  Grid, Cell, Tabs, TabHeader, TabHeaders, TabBody, TabContent, Link,
} from '@hyphengroup/cag-components';
import { Query } from 'react-apollo';
import PropTypes from 'prop-types';
import { mergeDeepLeft } from 'ramda';
import { getImageUrl } from 'helpers/Utils';
import {
  withTracking, widgetLoadTrack, widgetTabLoadTrack, widgetApplyNowTrack, widgetNavigationTrack,
} from 'components/tracking';
import withApplicationConfig from 'containers/withApplicationConfig';
import { nativeTemplate } from '../common/helpers/Utils';
import Queries from '../../queries';
import Vertical from './Vertical';
import {
  decodeUrlParam,
} from './WidgetUtils';

function Widget({
  verticals, match: { params: { hash } }, track, staticKeys, verticalRoutes, ccProductPageRoutes,
}) {
  const { version, title, language } = decodeUrlParam(hash);
  const isTabInitialLoadRef = useRef(true);

  let widgetTitle = '';
  let widgetLanguage = '';
  switch (version) {
    case (1): {
      widgetTitle = title;
      widgetLanguage = language;
      break;
    }
    default: {
      return <Fragment />;
    }
  }
  return (
    <Query
      query={Queries.GetAffiliateWidget}
      variables={{
        language: widgetLanguage,
        widgetTitle,
        staticTextKeys: Object.values(staticKeys),
      }}
    >
      {({ data, loading }) => {
        if (!data || loading || !data.widget) {
          return null;
        }
        const {
          widget: { data: widgetData, config: [widgetConfig], staticPageText: { staticText } },
        } = data;
        const { affiliateWidgetConfig } = widgetConfig;
        widgetLoadTrack(track, widgetTitle);
        const af = {};
        verticals.forEach(vertical => {
          const ctaBtnData = {
            linkVerticalRoute: verticalRoutes.find((routeItem) => routeItem[vertical])?.[vertical],
            staticKeys,
            staticText,
          };
          af[vertical] = [];
          widgetData[vertical].forEach((v, i) => {
            widgetData[vertical][i].list.ctaBtnData = ctaBtnData;
            af[vertical].push(mergeDeepLeft(
              widgetData[vertical][i],
              affiliateWidgetConfig[vertical][i]
            ));
          });
        });
        const affiliateWidget = mergeDeepLeft(af, affiliateWidgetConfig);

        const chosenVerticals = verticals
          .filter(v => affiliateWidget[v].length > 0) // Only choose verticals with products
          .map(v => affiliateWidget[v][0]);

        const sortedVerticals = chosenVerticals
          .filter(a => a.list) // Only choose verticals a list
          .sort((a, b) => a.list.orderPriority - b.list.orderPriority)
          .slice(0, 3); // Only choose 3 verticals

        const trackCtaClick = (tabLabel, product, positionInList, vertical) => {
          widgetApplyNowTrack(track, tabLabel, product, positionInList, vertical);
        };
        const trackTabChange = (products, tabLabel, vertical) => {
          widgetTabLoadTrack(track, tabLabel, products, vertical);

          if (!isTabInitialLoadRef.current) {
            widgetNavigationTrack(track, {
              componentType: 'tab-select',
              title: tabLabel,
            });
          }

          isTabInitialLoadRef.current = false;
        };
        const handleSeeMoreButtonClick = () => widgetNavigationTrack(track, {
          componentType: 'button',
          title: 'see more',
        });
        const handleSiteLogoClick = () => widgetNavigationTrack(track, {
          componentType: 'top-nav',
          title: 'top left logo',
        });
        const handlePromotionalImageClick = () => widgetNavigationTrack(track, {
          componentType: 'hero-image',
          title: widgetTitle,
        });

        return (
          <Grid height="600px" width="300px" columns="300px" rows="40px 134px 1fr" gap="0px" justifyContent="start">
            <Cell>
              <div style={{
                border: '1px solid #f5f5f5',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
              }}
              >
                <Link
                  to={affiliateWidget?.siteLogo?.link?.href}
                  variant="image"
                  onClick={handleSiteLogoClick}
                >
                  <img alt="site logo" src={getImageUrl(affiliateWidget?.siteLogo?.image?.url)} height="26px" width="96px" />
                </Link>
                <div
                  className="fb-like"
                  data-href={affiliateWidget?.facebookLikeButton?.href}
                  data-width="61px"
                  data-layout="button_count"
                  data-action="like"
                  data-size="small"
                  data-show-faces="false"
                  data-share="false"
                />
              </div>
            </Cell>
            <Cell>
              <Link
                to={nativeTemplate(
                  affiliateWidget?.promotionalImage?.link?.href, { title: widgetTitle }
                )
                }
                variant="image"
                onClick={handlePromotionalImageClick}
              >
                <img data-selector="promotionalImage" src={getImageUrl(affiliateWidget?.promotionalImage?.image?.url)} height="134px" width="300px" alt="Promotional Banner" />
              </Link>
            </Cell>
            <Cell>

              <Tabs style={{ height: '100%' }}>
                <TabHeaders dataSelectorId="affiliate-widget" dataSelectorType="tabHeaders">
                  {
                    sortedVerticals.map(v => <TabHeader key={v.tabLabel}>{v.tabLabel}</TabHeader>)
                  }
                </TabHeaders>
                <TabBody dataSelectorId="affiliate-widget" dataSelectorType="tabBody">
                  {
                    sortedVerticals.map(v => (
                      <TabContent border="1px solid #f5f5f5" bg="white" key={v.tabLabel} style={{ display: 'flex', flexDirection: 'column' }}>
                        <Vertical
                          {...v}
                          language={widgetLanguage}
                          widgetTitle={widgetTitle}
                          ccProductPageRoutes={ccProductPageRoutes}
                          trackCtaClick={trackCtaClick}
                          trackTabChange={trackTabChange}
                          onSeeMoreButtonClick={handleSeeMoreButtonClick}
                        />
                      </TabContent>
                    ))
                  }
                </TabBody>
              </Tabs>
            </Cell>
          </Grid>
        );
      }}
    </Query>
  );
}

Widget.propTypes = {
  verticals: PropTypes.arrayOf(PropTypes.string),
  match: PropTypes.shape({
    params: PropTypes.shape({
      hash: PropTypes.string,
    }),
  }).isRequired,
  track: PropTypes.func,
  staticKeys: PropTypes.shape({}),
  verticalRoutes: PropTypes.arrayOf(PropTypes.shape({})),
  ccProductPageRoutes: PropTypes.string.isRequired,
};

Widget.defaultProps = {
  verticals: ['cc', 'ba', 'pl', 'ti'],
  track: null,
  staticKeys: {
    LEARN_MORE_CTA_LABEL: '',
  },
  verticalRoutes: [{}],
};

export default withTracking(withApplicationConfig(Widget, ['routes', 'affiliatesConfig']));
